import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Spinner, Badge } from '../components/ui';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
}

function HealthScore({ score }) {
  const color = score >= 80 ? '#16a34a' : score >= 60 ? '#d97706' : '#dc2626';
  const label = score >= 80 ? 'Good' : score >= 60 ? 'Needs attention' : 'Action required';
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;

  return (
    <Card style={{ textAlign:'center', padding:32 }}>
      <p style={{ fontSize:13, fontWeight:600, color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:16 }}>
        Compliance Health Score
      </p>
      <div style={{ position:'relative', display:'inline-block', marginBottom:12 }}>
        <svg width={120} height={120} style={{ transform:'rotate(-90deg)' }}>
          <circle cx={60} cy={60} r={54} fill="none" stroke="#f3f4f6" strokeWidth={10} />
          <circle cx={60} cy={60} r={54} fill="none" stroke={color} strokeWidth={10}
            strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition:'stroke-dashoffset 1s ease' }} />
        </svg>
        <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          <span style={{ fontSize:28, fontWeight:700, color }}>{score}</span>
          <span style={{ fontSize:11, color:'var(--ink-3)' }}>/100</span>
        </div>
      </div>
      <p style={{ fontSize:14, fontWeight:600, color }}>{label}</p>
    </Card>
  );
}

function computeHealthScore(properties, certsMap, depositsMap, docsMap) {
  if (!properties.length) return 100;
  let total = 0, count = 0;

  properties.forEach(p => {
    if (!p.setup_complete) { total += 50; count++; return; }
    let score = 100;
    const certs = certsMap[p.id] || [];
    const deps  = depositsMap[p.id] || [];
    const docs  = docsMap[p.id] || [];

    // Certs (-15 per expired, -8 per due soon, -10 per missing)
    const required = ['epc','eicr','smoke_alarm','co_alarm'];
    if (p.has_gas) required.push('gas_safety');
    const certMap = Object.fromEntries(certs.map(c => [c.cert_type, c]));
    required.forEach(t => {
      const c = certMap[t];
      if (!c) score -= 10;
      else if (c.status === 'expired') score -= 15;
      else if (c.status === 'due_soon') score -= 8;
    });

    // Deposits (-20 if unprotected)
    deps.forEach(d => {
      if (d.protection_status === 'overdue') score -= 20;
      else if (d.protection_status === 'missing') score -= 10;
      if (d.prescribed_status === 'overdue') score -= 10;
    });

    // Docs (-5 per missing required doc)
    const servedTypes = new Set(docs.map(d => d.doc_type));
    ['epc','gas_safety','how_to_rent'].forEach(t => {
      if (!servedTypes.has(t)) score -= 5;
    });

    total += Math.max(0, score);
    count++;
  });

  return count ? Math.round(total / count) : 100;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [properties, setProperties]   = useState([]);
  const [certsMap, setCertsMap]       = useState({});
  const [depositsMap, setDepositsMap] = useState({});
  const [docsMap, setDocsMap]         = useState({});
  const [tasks, setTasks]             = useState([]);
  const [rentPayments, setRentPayments] = useState([]);
  const [loading, setLoading]         = useState(true);

  const ac = localStorage.getItem('lc_access_code');
  const h = () => ({ Authorization:`Bearer ${localStorage.getItem('token')}`, ...(ac ? { 'X-Access-Code': ac } : {}) });

  useEffect(() => {
    async function load() {
      const [props, tsks, rents] = await Promise.all([
        fetch('/api/properties',   { headers: h() }).then(r => r.json()),
        fetch('/api/tasks',        { headers: h() }).then(r => r.json()),
        fetch('/api/rent-payments',{ headers: h() }).then(r => r.json()),
      ]);

      const ps = Array.isArray(props) ? props : [];
      setProperties(ps);
      setTasks(Array.isArray(tsks) ? tsks : []);
      setRentPayments(Array.isArray(rents) ? rents : []);

      // Load certs, deposits, docs per property
      const cm = {}, dm = {}, docm = {};
      await Promise.all(ps.map(async p => {
        const [c, d, doc] = await Promise.all([
          fetch(`/api/properties/${p.id}/certificates`,    { headers: h() }).then(r => r.json()),
          fetch(`/api/properties/${p.id}/deposits`,        { headers: h() }).then(r => r.json()),
          fetch(`/api/properties/${p.id}/document-serving`,{ headers: h() }).then(r => r.json()),
        ]);
        cm[p.id]   = Array.isArray(c)   ? c   : [];
        dm[p.id]   = Array.isArray(d)   ? d   : [];
        docm[p.id] = Array.isArray(doc) ? doc : [];
      }));

      setCertsMap(cm);
      setDepositsMap(dm);
      setDocsMap(docm);
      setLoading(false);

      // Auto-generate tasks
      fetch('/api/tasks/generate', { method:'GET', headers: h() });
    }
    load();
  }, []);

  if (loading) return <Spinner />;

  const score = computeHealthScore(properties, certsMap, depositsMap, docsMap);

  // Issues across all properties
  const allCerts = Object.values(certsMap).flat();
  const expiredCerts  = allCerts.filter(c => c.status === 'expired');
  const dueSoonCerts  = allCerts.filter(c => c.status === 'due_soon');
  const allDeposits   = Object.values(depositsMap).flat();
  const depositIssues = allDeposits.filter(d => d.protection_status !== 'ok' || d.prescribed_status !== 'ok');
  const overdueRents  = rentPayments.filter(r => r.status === 'late' || (r.status === 'due' && new Date(r.due_date) < new Date()));
  const openTasks     = tasks.filter(t => t.status === 'open');
  const highTasks     = openTasks.filter(t => t.priority === 'high');

  const firstName = user?.full_name?.split(' ')[0] || 'there';

  return (
    <div className="fade-up">
      <div style={{ marginBottom:32 }}>
        <h1 style={{ fontSize:32, fontWeight:300 }}>Good morning, <em>{firstName}</em></h1>
        <p style={{ color:'var(--ink-3)', marginTop:4 }}>Here's your compliance overview across {properties.length} propert{properties.length !== 1 ? 'ies' : 'y'}</p>
      </div>

      {/* Top row — health score + key stats */}
      <div style={{ display:'grid', gridTemplateColumns:'min(240px, 100%) minmax(280px, 1fr)', gap:20, marginBottom:24 }}>
        <HealthScore score={score} />

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12 }}>
          {[
            { label:'Properties', value:properties.length, icon:'🏘', to:'/properties', color:'var(--ink)' },
            { label:'Expired certs', value:expiredCerts.length, icon:'🔴', to:'/properties', color: expiredCerts.length ? '#dc2626' : 'var(--ink)' },
            { label:'Expiring soon', value:dueSoonCerts.length, icon:'🟡', to:'/properties', color: dueSoonCerts.length ? '#d97706' : 'var(--ink)' },
            { label:'Deposit issues', value:depositIssues.length, icon:'💰', to:'/deposits', color: depositIssues.length ? '#dc2626' : 'var(--ink)' },
            { label:'Overdue rent', value:overdueRents.length, icon:'💷', to:'/rent', color: overdueRents.length ? '#dc2626' : 'var(--ink)' },
            { label:'Open tasks', value:openTasks.length, icon:'✅', to:'/tasks', color: highTasks.length ? '#dc2626' : 'var(--ink)' },
          ].map(s => (
            <Link key={s.label} to={s.to} style={{ textDecoration:'none' }}>
              <Card style={{ padding:'16px 20px', cursor:'pointer', transition:'border-color .15s' }}>
                <p style={{ fontSize:22 }}>{s.icon}</p>
                <p style={{ fontSize:28, fontWeight:700, color:s.color, lineHeight:1.1, marginTop:4 }}>{s.value}</p>
                <p style={{ fontSize:12, color:'var(--ink-3)', marginTop:2 }}>{s.label}</p>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:20 }}>
        {/* Priority tasks */}
        <Card>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16 }}>
            <h3 style={{ fontSize:17 }}>Priority Tasks</h3>
            <Link to="/tasks" style={{ fontSize:13, color:'var(--accent)' }}>View all →</Link>
          </div>
          {openTasks.length === 0 ? (
            <p style={{ color:'var(--ink-3)', fontSize:14 }}>✅ No open tasks — you're on top of everything!</p>
          ) : (
            openTasks.slice(0, 5).map(t => (
              <div key={t.id} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                <span style={{ fontSize:16, marginTop:1 }}>{t.priority === 'high' ? '🔴' : t.priority === 'medium' ? '🟡' : '🟢'}</span>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:14, fontWeight:500 }}>{t.title}</p>
                  {t.due_date && <p style={{ fontSize:12, color: t.days_until_due !== null && t.days_until_due <= 7 ? '#dc2626' : 'var(--ink-3)' }}>Due {formatDate(t.due_date)}{t.days_until_due !== null ? ` (${t.days_until_due}d)` : ''}</p>}
                </div>
              </div>
            ))
          )}
        </Card>

        {/* Upcoming deadlines */}
        <Card>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16 }}>
            <h3 style={{ fontSize:17 }}>Upcoming Deadlines</h3>
            <Link to="/calendar" style={{ fontSize:13, color:'var(--accent)' }}>Calendar →</Link>
          </div>
          {(() => {
            const upcoming = allCerts
              .filter(c => c.expiry_date)
              .map(c => ({ ...c, days: Math.ceil((new Date(c.expiry_date) - new Date()) / 86400000) }))
              .filter(c => c.days >= 0 && c.days <= 90)
              .sort((a, b) => a.days - b.days)
              .slice(0, 5);

            if (!upcoming.length) return <p style={{ color:'var(--ink-3)', fontSize:14 }}>No deadlines in the next 90 days 🎉</p>;

            const certLabels = { epc:'EPC', gas_safety:'Gas Safety', eicr:'EICR', smoke_alarm:'Smoke Alarm', co_alarm:'CO Alarm', legionella:'Legionella' };
            return upcoming.map(c => (
              <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                <div>
                  <p style={{ fontSize:14, fontWeight:500 }}>{certLabels[c.cert_type] || c.cert_type}</p>
                  <p style={{ fontSize:12, color:'var(--ink-3)' }}>{c.property_address || ''}</p>
                </div>
                <div style={{ textAlign:'right' }}>
                  <p style={{ fontSize:13, color: c.days <= 30 ? '#dc2626' : c.days <= 60 ? '#d97706' : '#16a34a', fontWeight:600 }}>{c.days}d</p>
                  <p style={{ fontSize:11, color:'var(--ink-3)' }}>{formatDate(c.expiry_date)}</p>
                </div>
              </div>
            ));
          })()}
        </Card>

        {/* Recent rent payments */}
        <Card>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16 }}>
            <h3 style={{ fontSize:17 }}>Rent Overview</h3>
            <Link to="/rent" style={{ fontSize:13, color:'var(--accent)' }}>View all →</Link>
          </div>
          {rentPayments.length === 0 ? (
            <p style={{ color:'var(--ink-3)', fontSize:14 }}>No rent payments recorded yet. <Link to="/rent" style={{ color:'var(--accent)' }}>Add rent →</Link></p>
          ) : (
            rentPayments.slice(0, 5).map(r => (
              <div key={r.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                <div>
                  <p style={{ fontSize:14, fontWeight:500 }}>{r.tenant_name}</p>
                  <p style={{ fontSize:12, color:'var(--ink-3)' }}>{r.property_address}</p>
                </div>
                <div style={{ textAlign:'right' }}>
                  <p style={{ fontSize:14, fontWeight:600 }}>£{(r.amount_gbp / 100).toFixed(0)}</p>
                  <Badge status={r.status === 'paid' ? 'valid' : r.status === 'late' ? 'expired' : 'due_soon'} label={r.status} />
                </div>
              </div>
            ))
          )}
        </Card>

        {/* Compliance alerts */}
        <Card>
          <div style={{ marginBottom:16 }}>
            <h3 style={{ fontSize:17 }}>Compliance Alerts</h3>
          </div>
          {expiredCerts.length === 0 && depositIssues.length === 0 ? (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <p style={{ fontSize:32, marginBottom:8 }}>✅</p>
              <p style={{ fontWeight:600, marginBottom:4 }}>All clear!</p>
              <p style={{ color:'var(--ink-3)', fontSize:14 }}>No compliance issues detected across your portfolio.</p>
            </div>
          ) : (
            <>
              {expiredCerts.map(c => (
                <div key={c.id} style={{ display:'flex', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                  <span>🔴</span>
                  <div>
                    <p style={{ fontSize:14, fontWeight:500, color:'#dc2626' }}>Expired: {c.cert_type?.replace('_',' ')}</p>
                    <p style={{ fontSize:12, color:'var(--ink-3)' }}>Expired {formatDate(c.expiry_date)}</p>
                  </div>
                </div>
              ))}
              {depositIssues.map(d => (
                <div key={d.id} style={{ display:'flex', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                  <span>🟡</span>
                  <div>
                    <p style={{ fontSize:14, fontWeight:500, color:'#d97706' }}>Deposit issue: {d.property_address}</p>
                    <p style={{ fontSize:12, color:'var(--ink-3)' }}>Protection or prescribed info missing</p>
                  </div>
                </div>
              ))}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}